/**
 * Design — a saved building-geometry SCENARIO for a project (spec §17):
 *
 *   'existing'  — the factual representation of the property as it is
 *                 (normally one canonical Existing design per project);
 *   'proposed'  — a planning alternative ("Open Kitchen", "Keep Wall"),
 *                 any number, created by duplicating Existing (or another
 *                 design, or blank for new construction).
 *
 * Duplication copies geometry with NEW UUIDs and records per-entity
 * lineage (origin_id → the entity it was duplicated from) so the future
 * Existing ↔ Proposed diff can understand what changed. Editing one
 * design never touches another. Designs are NOT undo history — undo/redo
 * remains editor history within the active design.
 *
 * One design per project is the SCOPE SOURCE (projects.active_design_id):
 * its geometry drives room measurements and geometry-bound feature
 * quantities. There is exactly one project financial truth — scenario
 * estimates/comparison arrive in a later phase.
 */

export type DesignKind = "existing" | "proposed";

export interface Design {
  id: string;
  projectId: string;
  name: string;
  kind: DesignKind;
  /** The design this one was duplicated from (lineage; null = original/blank). */
  sourceDesignId: string | null;
  sortOrder: number;
  notes: string;
  archivedAt: string | null;
  /**
   * WHO DREW IT (spec §65D). Null means the project owner did, which is what
   * every design predating sharing is. A collaborator-authored design
   * carries their account id, and only that account may edit it.
   */
  authorAccountId: string | null;
  /**
   * Bumped once per committed change. Not user-visible history: it exists so
   * a live viewer can notice it missed an update and resync (spec §65J).
   */
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export function validateDesignName(name: string): string[] {
  return name.trim() === "" ? ["Design name is required."] : [];
}
